import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Calendar } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { ChartContainer } from '@/components/ui/chart';
import type { PageCountPreference, PublicationYearTrend } from '@/lib/authorAnalyticsUtils';

interface BookCharacteristicsProps {
  pageCountData: PageCountPreference[];
  yearTrends: PublicationYearTrend[];
}

export function BookCharacteristics({ pageCountData, yearTrends }: BookCharacteristicsProps) {
  // Calculate average page count and preferences
  const avgPageCount = Math.round(
    pageCountData.reduce((sum, book) => sum + book.pageCount, 0) / pageCountData.length
  );

  const shortBooks = pageCountData.filter(b => b.pageCount < 250).length;
  const mediumBooks = pageCountData.filter(b => b.pageCount >= 250 && b.pageCount < 450).length;
  const longBooks = pageCountData.filter(b => b.pageCount >= 450).length;

  const scatterConfig = {
    rating: {
      label: "Rating",
      color: "hsl(var(--primary))",
    },
  };

  const barConfig = {
    count: {
      label: "Books",
      color: "hsl(var(--accent))",
    },
  };

  return (
    <div className="space-y-6">
      {/* Page Count Preferences Scatter */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Page Count Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-6">
            Your ratings vs. book length - discover your sweet spot
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="text-xl font-bold text-primary">{avgPageCount}</div>
              <p className="text-xs text-muted-foreground mt-1">Avg Pages</p>
            </div>
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="text-xl font-bold text-secondary">{shortBooks}</div>
              <p className="text-xs text-muted-foreground mt-1">&lt;250 pages</p>
            </div>
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="text-xl font-bold text-accent">{mediumBooks}</div>
              <p className="text-xs text-muted-foreground mt-1">250-450</p>
            </div>
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="text-xl font-bold text-star">{longBooks}</div>
              <p className="text-xs text-muted-foreground mt-1">450+ pages</p>
            </div>
          </div>

          <ChartContainer config={scatterConfig} className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  type="number" 
                  dataKey="pageCount" 
                  name="Pages"
                  stroke="hsl(var(--muted-foreground))"
                  label={{ value: 'Page Count', position: 'insideBottom', offset: -10, fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  type="number" 
                  dataKey="rating" 
                  name="Rating"
                  domain={[0, 5]}
                  stroke="hsl(var(--muted-foreground))"
                  label={{ value: 'Your Rating', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                  formatter={(value: any, name: string, props: any) => {
                    if (name === 'pageCount') return [value + ' pages', 'Length'];
                    if (name === 'rating') return ['⭐ ' + value, 'Rating'];
                    return [value, name];
                  }}
                  content={({ payload }) => {
                    if (!payload || payload.length === 0) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
                        <p className="font-medium mb-1">{data.title}</p>
                        <p className="text-xs text-muted-foreground mb-2">
                          {data.authors.join(', ')}
                        </p>
                        <div className="flex items-center gap-3 text-xs">
                          <span>📖 {data.pageCount} pages</span>
                          <span>⭐ {data.rating}</span>
                        </div>
                      </div>
                    );
                  }}
                />
                <Scatter 
                  name="Books" 
                  data={pageCountData} 
                  fill="hsl(var(--primary))"
                  fillOpacity={0.6}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Publication Year Trends */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-accent" />
            Publication Year Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-6">
            Which decades do you read from most?
          </p>

          <ChartContainer config={barConfig} className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearTrends} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="decade" 
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Books', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                  formatter={(value: number, name: string, props: any) => [
                    `${value} books (⭐ ${props.payload.averageRating.toFixed(1)} avg)`,
                    props.payload.decade
                  ]}
                />
                <Bar 
                  dataKey="count" 
                  fill="hsl(var(--accent))"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}
